import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { BUTTON_GROUP_SELECTOR, DRAG_BUTTON_CLASS_NAME } from '@ardoq/button';
import { colors, r8, s16, s24, s32, s8 } from '@ardoq/design-tokens';
import {
  StatusType,
  Tag,
  TAG_BUTTON_SELECTOR,
  TAG_DIV_SELECTOR,
} from '@ardoq/status-ui';
import { text1 } from '@ardoq/typography';
import { BooleanOperator } from '@ardoq/api-types';
import { RuleNotificationType } from '../types';
import { ARDOQ_LOADER_COMPONENT_SELECTOR } from '@ardoq/loader';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const FlexDiv = styled.div`
  min-width: 0;
`;

export const Flex = ({
  flex,
  width,
  children,
}: PropsWithChildren<{ flex?: number; width?: number }>) => (
  <FlexDiv style={{ flex, width }}>{children}</FlexDiv>
);

export const QueryBuilderReadOnlyText = styled.span`
  ${text1};
  line-height: 24px;
  white-space: break-spaces;
`;

export const ToLabel = styled.span`
  display: flex;
  align-items: center;
`;

export const QueryBuilderRuleWrapper = styled.div<{
  $hasError?: boolean;
  $isReadOnly?: boolean;
  $notificationType?: RuleNotificationType;
  $isCompact?: boolean;
}>`
  ${({ $isReadOnly, $isCompact }) =>
    !$isReadOnly &&
    !$isCompact &&
    css`
      height: 70px;
    `};
  flex: 1;
  border: 1px dashed ${colors.borderAction};
  background-color: ${colors.surfaceDefault};
  border-radius: ${r8};
  display: flex;
  gap: ${s8};
  align-items: center;
  padding: ${s16} ${s8};

  ${({ $isCompact }) =>
    $isCompact
      ? css`
          align-items: flex-start;
        `
      : css`
          ${FlexDiv} {
            &:first-child {
              padding-left: ${s16};
            }
          }
        `}

  ${({ $hasError, $notificationType }) => {
    if ($hasError) {
      return $notificationType === RuleNotificationType.WARNING
        ? css`
            border-color: ${colors.borderWarningStrong};
            background-color: ${colors.surfaceWarning};
          `
        : css`
            border-color: ${colors.borderErrorStrong};
            background-color: ${colors.surfaceError};
          `;
    }
  }}
`;

export const QueryBuilderRuleInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${s8};
`;

export const QueryBuilderRuleNotificationMessage = styled.div<{
  $notificationType?: RuleNotificationType;
}>`
  ${text1};
  padding-left: ${s16};
  color: ${({ $notificationType }) =>
    $notificationType === RuleNotificationType.WARNING
      ? colors.textWarning
      : colors.textError};
  flex: 1;
`;

export const SubqueryHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  position: relative;
  .${DRAG_BUTTON_CLASS_NAME} {
    margin-left: -${s8};
    margin-right: ${s8};
  }
  ${ARDOQ_LOADER_COMPONENT_SELECTOR} {
    position: absolute;
    left: -${s24};
  }
`;

export const ConditionSelectWrapper = styled.div`
  width: 120px;
`;

export const SearchContextSelectWrapper = styled.div`
  width: 180px;
`;

export const SubqueryHeader = styled.div<{ $isDisabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
  ${text1};

  ${ConditionSelectWrapper} {
    margin: 0 ${s8};
    &.first-condition {
      margin-left: 0;
    }
  }

  ${SearchContextSelectWrapper} {
    margin: 0 ${s8};
  }
`;

const hintLineCss = css`
  display: block;
  width: 37px;
  left: 20px;
  content: ' ';
  position: absolute;
`;

export const InvisibleDragOverBar = styled.div`
  height: ${s16};
  width: 100%;
  position: absolute;
  left: 0;
  &:first-child {
    top: -${s16};
  }
  &:last-child {
    bottom: -${s16};
  }
`;

export const QueryBuilderRuleAndConditionWrapper = styled.div<{
  $isFirst?: boolean;
  $isLast?: boolean;
  $isCompact?: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  position: relative;

  ${({ $isCompact }) =>
    $isCompact
      ? css`
          min-width: 340px;
          gap: ${s8};
          flex-direction: column;
        `
      : css`
          gap: ${s16};
          align-items: center;
        `}

  ${({ $isFirst, $isLast, $isCompact, $isDisabled }) =>
    ($isFirst && $isLast && css``) ||
    ($isCompact && css``) ||
    css`
      &::before {
        ${hintLineCss};
        height: calc(100% + 29px);
        top: 35px;
        border-radius: ${r8} 0 0 0;
        opacity: ${$isDisabled ? 0.5 : 1};
        border-top: 2px dashed ${colors.borderInfoStrong};
        border-left: 2px dashed
          ${$isLast ? 'transparent' : colors.borderInfoStrong};
      }
    `}
  ${({ $isCompact, $isFirst }) =>
    $isCompact &&
    !$isFirst &&
    css`
      padding-top: ${s8};
    `}

  ${({ $isFirst, $isCompact }) =>
    $isFirst
      ? css`
          & > ${ConditionTagWrapper} {
            opacity: 0;
          }
        `
      : !$isCompact &&
        css`
          margin-top: ${s16};
        `}
`;

export const SubqueryWrapper = styled.div<{
  $level: number;
  $isReadOnly: boolean;
  $isCompact?: boolean;
}>`
  min-width: fit-content;
  flex: 1;
  border-radius: ${r8};
  background-color: ${({ $level }) =>
    $level % 2 ? colors.surfaceStatusNeutral : colors.surfaceDefault};
  display: flex;
  flex-direction: column;
  ${({ $isReadOnly }) =>
    $isReadOnly &&
    css`
      padding-bottom: ${s16};
    `};
  & > ${SubqueryHeaderWrapper} {
    margin-bottom: ${s32};
  }

  ${({ $level, $isCompact }) =>
    $level === 0
      ? css`
          & > ${BUTTON_GROUP_SELECTOR} {
            ${
              $isCompact
                ? css`
                    margin-top: ${s16};
                  `
                : css`
                    margin: ${s16} 0 0 56px;
                  `
            }
          }
        `
      : css`
          & > ${SubqueryHeaderWrapper} {
            margin: ${s16};
          }

          & > ${QueryBuilderRuleAndConditionWrapper} {
            ${
              $isCompact
                ? css`
                    margin: 0 ${s16};
                  `
                : css`
                    margin: ${s16} ${s16} 0;
                  `
            }
          }

          & > ${BUTTON_GROUP_SELECTOR} {
            width: max-content;
            margin: ${$isCompact ? s16 : `${s16} ${s16} ${s16} 72px`};
            &:last-child {
              margin-bottom: ${s16};
            }
          }
        `};
`;

export const QueryBuilderWrapper = styled.div<{
  $skipStyles?: boolean;
  $isCompact?: boolean;
}>`
  position: relative;
  ${({ $isCompact }) =>
    !$isCompact &&
    css`
      min-width: 600px;
    `}
  ${({ $skipStyles }) =>
    !$skipStyles &&
    css`
      border: 1px solid ${colors.borderAction};
      border-radius: ${r8};
      padding: ${s16} 0;

      > ${SubqueryWrapper}, > ${BUTTON_GROUP_SELECTOR} {
        margin: 0 ${s32};
      }
    `}
`;

const ConditionTagWrapper = styled.div<{
  $isCompact?: boolean;
  $isDisabled?: boolean;
}>`
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
  ${({ $isCompact }) =>
    !$isCompact &&
    css`
      display: flex;
      justify-content: center;
      align-self: baseline;
      top: ${s24};
    `}
  ${({ $isCompact, $isDisabled }) =>
    $isCompact &&
    css`
      &::before {
        content: '';
        opacity: ${$isDisabled ? 0.5 : 1};
        position: absolute;
        top: -${s8};
        left: 20px;
        display: block;
        width: 1px;
        height: 40px;
        border-left: 2px dotted ${colors.borderInfoStrong};
      }
    `}

  ${TAG_DIV_SELECTOR}, ${TAG_BUTTON_SELECTOR} {
    width: 40px;
    justify-content: center;
  }
`;

type ConditionTagProps = {
  condition: BooleanOperator;
  isCompact?: boolean;
  isDisabled?: boolean;
  i18nLocale?: I18nLocale;
};
export const ConditionTag = ({
  condition,
  isCompact,
  isDisabled,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ConditionTagProps) => (
  <ConditionTagWrapper $isCompact={isCompact} $isDisabled={isDisabled}>
    <Tag
      label={
        condition === BooleanOperator.AND
          ? t(i18nLocale, 'queryBuilder:condition.and', 'And')
          : t(i18nLocale, 'queryBuilder:condition.or', 'Or')
      }
      statusType={StatusType.INFO}
    />
  </ConditionTagWrapper>
);

const BAR_COLOR = colors.borderInfoStrong;
export const DropPlaceholderBar = styled.div<{
  $isSubqueryContext?: boolean;
  $isAbove?: boolean;
  $isFirst?: boolean;
}>`
  position: relative;
  border-bottom: 3px solid ${BAR_COLOR};
  border-radius: 5px;
  ${({ $isAbove, $isSubqueryContext, $isFirst }) => css`
    margin-top: ${$isAbove ? s16 : s16};
    margin-right: ${$isSubqueryContext ? s16 : 0};
    margin-bottom: ${!$isSubqueryContext && $isFirst && $isAbove ? s16 : 0};
    margin-left: ${$isSubqueryContext ? '72px' : '56px'};
  `}

  &::before {
    position: absolute;
    left: 0;
    top: -3px;
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: ${BAR_COLOR};
    content: ' ';
  }
`;
